import { tokens } from '@/lib/tokens.js';
import { useState } from 'react';
import { useCreateRole, useUpdateRole } from '../../hooks/use-admin-roles.js';
import type { CreateRoleBody, RoleDetail, UpdateRoleBody } from '../../lib/admin-client.js';

const s = {
  container: {
    backgroundColor: tokens.colors.bgCardDark,
    borderRadius: tokens.radius.card,
    boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
    overflow: 'hidden',
  },
  header: {
    padding: '1rem 1.5rem',
    borderBottom: `1px solid ${tokens.colors.borderDark}`,
  },
  title: { fontSize: '1rem', fontWeight: 600, color: tokens.colors.textDark, margin: 0 },
  body: { padding: '1.5rem', display: 'flex', flexDirection: 'column' as const, gap: '1rem' },
  group: { display: 'flex', flexDirection: 'column' as const, gap: '0.375rem' },
  label: { fontSize: '0.875rem', fontWeight: 500, color: tokens.colors.textDark },
  input: {
    backgroundColor: '#0f172a',
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.input,
    color: tokens.colors.textDark,
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box' as const,
  },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    paddingTop: '0.5rem',
    borderTop: `1px solid ${tokens.colors.borderDark}`,
    marginTop: '0.5rem',
  },
  btnPrimary: {
    backgroundColor: tokens.colors.primary,
    color: '#fff',
    border: 'none',
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    fontWeight: 500,
  },
  btnCancel: {
    backgroundColor: 'transparent',
    color: tokens.colors.textDark,
    border: `1px solid ${tokens.colors.borderDark}`,
    borderRadius: tokens.radius.button,
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
};

interface RoleFormProps {
  tenantId: string;
  existingRole?: RoleDetail;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function RoleForm({ tenantId, existingRole, onSuccess, onCancel }: RoleFormProps) {
  const isEdit = existingRole !== undefined;

  const [code, setCode] = useState(existingRole?.code ?? '');
  const [name, setName] = useState(existingRole?.name ?? '');
  const [description, setDescription] = useState(existingRole?.description ?? '');
  const [isDefault, setIsDefault] = useState(existingRole?.is_default ?? false);
  const [iconLabel, setIconLabel] = useState(existingRole?.icon.label ?? '');
  const [gradient0, setGradient0] = useState(existingRole?.icon.gradient[0] ?? '#374151');
  const [gradient1, setGradient1] = useState(existingRole?.icon.gradient[1] ?? '#1f2937');

  const createRole = useCreateRole(tenantId);
  const updateRole = useUpdateRole(tenantId);

  const isPending = createRole.isPending || updateRole.isPending;
  const isError = createRole.isError || updateRole.isError;

  function handleSubmit() {
    if (isEdit && existingRole) {
      const body: UpdateRoleBody = {
        name,
        ...(description ? { description } : {}),
        is_default: isDefault,
        icon_label: iconLabel,
        icon_gradient: [gradient0, gradient1],
      };
      updateRole.mutate({ roleId: existingRole.id, body }, { ...(onSuccess && { onSuccess }) });
    } else {
      const body: CreateRoleBody = {
        code,
        name,
        ...(description ? { description } : {}),
        is_default: isDefault,
        icon_label: iconLabel,
        icon_gradient: [gradient0, gradient1],
      };
      createRole.mutate(body, { ...(onSuccess && { onSuccess }) });
    }
  }

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h2 style={s.title}>{isEdit ? 'Edit Role' : 'New Role'}</h2>
      </div>
      <div style={s.body}>
        {!isEdit && (
          <div style={s.group}>
            <label style={s.label} htmlFor="role-code">
              Code
            </label>
            <input
              id="role-code"
              style={s.input}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. commercial"
              pattern="[a-z][a-z0-9_]*"
              aria-label="Role code"
            />
          </div>
        )}
        <div style={s.group}>
          <label style={s.label} htmlFor="role-name">
            Name
          </label>
          <input
            id="role-name"
            style={s.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Role display name"
            aria-label="Role name"
          />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="role-desc">
            Description
          </label>
          <input
            id="role-desc"
            style={s.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            aria-label="Role description"
          />
        </div>
        <div style={s.checkRow}>
          <input
            type="checkbox"
            id="role-default"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            aria-label="Set as default role"
          />
          <label style={s.label} htmlFor="role-default">
            Default role
          </label>
          {isDefault && (
            <span style={{ fontSize: '0.75rem', color: tokens.colors.warning }}>
              Warning: changing the default role affects all tenants.
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <div style={{ ...s.group, flex: 1 }}>
            <label style={s.label} htmlFor="role-icon">
              Icon label
            </label>
            <input
              id="role-icon"
              style={s.input}
              value={iconLabel}
              onChange={(e) => setIconLabel(e.target.value)}
              maxLength={4}
              placeholder="e.g. COM"
              aria-label="Icon label"
            />
          </div>
          <div style={s.group}>
            <label style={s.label} htmlFor="gradient-0">
              Gradient start
            </label>
            <input
              type="color"
              id="gradient-0"
              value={gradient0}
              onChange={(e) => setGradient0(e.target.value)}
              aria-label="Gradient start color"
            />
          </div>
          <div style={s.group}>
            <label style={s.label} htmlFor="gradient-1">
              Gradient end
            </label>
            <input
              type="color"
              id="gradient-1"
              value={gradient1}
              onChange={(e) => setGradient1(e.target.value)}
              aria-label="Gradient end color"
            />
          </div>
        </div>

        {isError && (
          <p style={{ color: tokens.colors.error, fontSize: '0.875rem' }} role="alert">
            Error saving role. Please try again.
          </p>
        )}

        <div style={s.actions}>
          <button
            type="button"
            style={{ ...s.btnPrimary, opacity: isPending ? 0.7 : 1 }}
            onClick={handleSubmit}
            disabled={isPending || name.trim().length === 0}
            aria-label={isEdit ? 'Update role' : 'Create role'}
          >
            {isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
          </button>
          <button type="button" style={s.btnCancel} onClick={onCancel} aria-label="Cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
